import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { FormGroupToObject } from "src/app/pipes/form-group-to-object.pipe";
import { AuthStatusService } from "src/app/services/auth-status.service";
import { ExpertContractsService } from "src/app/services/expert-contracts.service";
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
  selector: "app-valet-contracts",
  templateUrl: "./valet-contracts.component.html",
  styleUrls: ["./valet-contracts.component.scss"],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0 }))
      ])
    ]),
    trigger('slideUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px) scale(0.95)' }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', 
          style({ opacity: 1, transform: 'translateY(0) scale(1)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', 
          style({ opacity: 0, transform: 'translateY(10px) scale(0.98)' }))
      ])
    ])
  ]
})
export class ValetContractsComponent implements OnInit {
  contracts: any[] = [];
  serviceFilterForm: any;
  filtered = {};
  limit: number;
  page: number;
  count: number;
  maxPage: number;
  order: {};
  public defaultPage = 1;
  public defaultLimit = 100;
  public defaultOrder = { "ExpertContracts.createdAt": "DESC" };
  groups: any[] = [];
  pdfUrl: SafeResourceUrl | null = null;
  showFallback = false;
  private rawPdfUrl: string = '';

  constructor(
    private expertContractsService: ExpertContractsService,
    private _snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
    private authStatusService: AuthStatusService,
    private sanitizer: DomSanitizer,
  ) {
    try {
      this.groups = this.authStatusService
        .getRoles()
        .map((elem: string) => elem.toLowerCase());
    } catch {
      this.groups = [];
    }
    this.manageContractFilterForm();
  }

  async ngOnInit() {
    await this.initialize();
  }

  async initialize() {
    await this.loadContracts();
  }

  openImage(event) {
    let w = window.open("");
    w.document.write(event.target.outerHTML);
  }

  manageContractFilterForm() {
    this.serviceFilterForm = new FormGroup({
      "ExpertContracts.id": new FormControl(""),
      'UserExpert.surname': new FormControl(''),
    });
  }

  filter() {
    let filterForm = new FormGroupToObject().transform(this.serviceFilterForm);
    this.filtered = {};
    
    if (filterForm['ExpertContracts.id'])
      this.filtered['ExpertContracts.id'] = filterForm['ExpertContracts.id'];
    if (filterForm['UserExpert.surname'])
      this.filtered['UserExpert.surname'] = { operator: 'like', values: [`%${filterForm['UserExpert.surname']}%`], concat: [{ string: ' ' }, { column: 'UserExpert.name' }] };

    this.loadContracts();
  }

  fnReset() {
    this.manageContractFilterForm();
  }

  async loadContracts(limit?: number, page?: number, order?: {}) {
    this.page =
      page ||
      parseInt(this.route.snapshot.queryParamMap.get("page")) ||
      this.defaultPage;
    this.limit =
      limit ||
      parseInt(this.route.snapshot.queryParamMap.get("limit")) ||
      this.defaultLimit;
    this.order =
      order ||
      JSON.parse(this.route.snapshot.queryParamMap.get("order")) ||
      this.defaultOrder;

    const contracts = await this.expertContractsService.getAll(
      this.page,
      this.limit,
      this.order,
      this.filtered,
    );
    this.contracts = contracts.contracts;
    this.count = contracts.count;
    this.maxPage = Math.ceil(this.count / this.limit);
  }

  openPdf(url: string) {
    this.rawPdfUrl = url;
    this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.showFallback = false;
  }

  closePdf() {
    this.pdfUrl = null;
    this.showFallback = false;
  }

  downloadPdf() {
    const link = document.createElement('a');
    link.href = this.rawPdfUrl;
    link.download = 'document.pdf';
    link.click();
  }

  openInNewTab() {
    window.open(this.rawPdfUrl, '_blank');
  }

  public navigateOrder(attribute: string | string[]) {
    delete this.order["ExpertContracts.updatedAt"];
    attribute = Array.isArray(attribute) ? attribute : [attribute];
    for (let attr of attribute) {
      switch (this.order[attr]) {
        case "ASC":
          this.order[attr] = "DESC";
          break;
        case "DESC":
          this.order[attr] = undefined;
          break;
        default:
          this.order = {};
          this.order[attr] = "ASC";
          break;
      }
    }

    this.navigatePage(this.limit, this.page, { order: this.order });
  }

  navigatePage(limit: number, page: number, args: {} = {}) {
    args = this.escapeQueryStringParams(args);
    const previousArgs = Object.keys(args).length
      ? {}
      : this.route.snapshot.queryParams;
    const newParams = { ...previousArgs };
    newParams[`limit`] = limit;
    newParams[`page`] = page;
    this.router
      .navigate(
        this.route.snapshot.url.map((u) => u.path),
        { queryParams: { ...newParams, ...args } },
      )
      .then((data) => {
        this.loadContracts();
      });
  }

  private escapeQueryStringParams(args: {} = {}): {} {
    Object.keys(args).forEach((key) => {
      switch (Object.prototype.toString.call(args[key])) {
        case "[object Number]":
        case "[object String]":
          // Keep the same
          break;
        case "[object Array]":
        case "[object Object]":
          // Convert to JSON
          args[key] = JSON.stringify(args[key]);
          break;
        default:
          // Not supported type
          delete args[key];
          break;
      }
    });
    return args;
  }

  public orderIcon(attribute: string | string[]): string {
    attribute = Array.isArray(attribute) ? attribute : [attribute];
    for (let attr of attribute) {
      switch (this.order[attr]) {
        case "ASC":
          return "fa-sort-up";
        case "DESC":
          return "fa-sort-down";
        default:
          return "fa-sort";
      }
    }
  }

  navigate(event) {
    this.loadContracts(event.limit, event.page, this.order);
  }
}
